import { createNextState as produce } from '@reduxjs/toolkit'

import {
	BLOCKS_PER_SLIDE_CAP,
	DEFAULT_LAYOUT,
	DEFAULT_THEME,
	DEFAULT_TRANSITION,
	LAYOUT_REGIONS,
	SLIDE_CAP,
} from '../constants'
import type { Block, DeckDefinition, EntityRef, Slide, SlideLayout, Story, Transition } from '../types/presentation.types'

/**
 * Pure operations on a deck definition (MYD-493).
 *
 * Every function takes a deck and returns a deck. When an operation does not
 * apply (an unknown id, a full slide, a region the layout lacks) it returns the
 * SAME object it was given. `useDeckEditor` relies on that identity to tell an
 * edit from a no-op, so a refused edit never lands on the undo stack.
 *
 * Built on Immer (RTK's `createNextState`), which gives structural sharing:
 * editing one block's config copies that block and its parents, not a
 * 100-slide deck, and unchanged slides keep their identity so memoised slide
 * thumbnails do not re-render.
 *
 * Ids are passed IN rather than generated here. React may run a reducer twice
 * in development, and an operation that minted its own id would then create a
 * block under one id and hand the caller another.
 */

/** A client id. The server keeps any short url-safe id it is given. */
export const newId = (): string =>
	typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
		? crypto.randomUUID()
		: `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

/**
 * JSON with object keys sorted, so two definitions that differ only in key
 * order compare equal. The server rebuilds objects in its own key order, and a
 * plain JSON.stringify comparison would call every freshly saved deck dirty.
 */
export const stableStringify = (value: unknown): string =>
	JSON.stringify(value, (_key, current) =>
		current && typeof current === 'object' && !Array.isArray(current)
			? Object.fromEntries(Object.entries(current).sort(([a], [b]) => a.localeCompare(b)))
			: current,
	)

export const emptyDeck = (): DeckDefinition => ({
	theme: DEFAULT_THEME,
	transition: DEFAULT_TRANSITION,
	stories: [],
})

export const makeStory = (id: string, { title = '', anchor = null }: Partial<Pick<Story, 'title' | 'anchor'>> = {}): Story => ({
	id,
	title,
	anchor,
	divider: false,
	slides: [],
})

export const makeSlide = (id: string, layout: SlideLayout = DEFAULT_LAYOUT): Slide => ({
	id,
	layout,
	title: '',
	subtitle: '',
	speakerNotes: '',
	transition: null,
	blocks: [],
})

export const makeBlock = (
	id: string,
	{ region, element, config = {}, source = null }: Pick<Block, 'region' | 'element'> & Partial<Pick<Block, 'config' | 'source'>>,
): Block => ({ id, region, element, source, config })

// =============================================================================
// Lookup
// =============================================================================

export const countSlides = (deck: DeckDefinition): number =>
	deck.stories.reduce((total, story) => total + story.slides.length, 0)

export const findSlide = (deck: DeckDefinition, slideId: string) => {
	for (const [storyIndex, story] of deck.stories.entries()) {
		const slideIndex = story.slides.findIndex(slide => slide.id === slideId)
		if (slideIndex >= 0) return { storyIndex, slideIndex, story, slide: story.slides[slideIndex] }
	}
	return null
}

export const findBlock = (deck: DeckDefinition, blockId: string) => {
	for (const [storyIndex, story] of deck.stories.entries()) {
		for (const [slideIndex, slide] of story.slides.entries()) {
			const blockIndex = slide.blocks.findIndex(block => block.id === blockId)
			if (blockIndex >= 0) return { storyIndex, slideIndex, blockIndex, story, slide, block: slide.blocks[blockIndex] }
		}
	}
	return null
}

const clampIndex = (index: number | undefined, length: number) =>
	index === undefined ? length : Math.max(0, Math.min(index, length))

/** Move an item within one array, in place. Used inside Immer recipes only. */
const moveWithin = <T>(items: T[], from: number, to: number) => {
	const [item] = items.splice(from, 1)
	items.splice(Math.max(0, Math.min(to, items.length)), 0, item)
}

// =============================================================================
// Deck
// =============================================================================

export const setDeckTransition = (deck: DeckDefinition, transition: Transition): DeckDefinition =>
	deck.transition === transition
		? deck
		: produce(deck, draft => {
				draft.transition = transition
			})

// =============================================================================
// Stories
// =============================================================================

export const addStory = (
	deck: DeckDefinition,
	id: string,
	{ title, anchor, index }: Partial<Pick<Story, 'title' | 'anchor'>> & { index?: number } = {},
): DeckDefinition =>
	produce(deck, draft => {
		draft.stories.splice(clampIndex(index, draft.stories.length), 0, makeStory(id, { title, anchor }))
	})

/**
 * Stories made elsewhere (MYD-505: from a template), put in as given apart
 * from their ids. Every story, slide and block gets a fresh one, so a story
 * made twice, or a server id that happens to be in use, can never collide.
 * `ids` supplies them in order (story, then each slide and its blocks), so the
 * caller knows the new story ids.
 */
export const insertStories = (deck: DeckDefinition, stories: Story[], ids: () => string, index?: number): DeckDefinition =>
	produce(deck, draft => {
		const fresh = stories.map(story => ({
			...story,
			id: ids(),
			slides: story.slides.map(slide => ({ ...slide, id: ids(), blocks: slide.blocks.map(block => ({ ...block, id: ids() })) })),
		}))
		draft.stories.splice(clampIndex(index, draft.stories.length), 0, ...fresh)
	})

export const updateStory = (
	deck: DeckDefinition,
	storyId: string,
	patch: Partial<Pick<Story, 'title' | 'anchor' | 'divider'>>,
): DeckDefinition => {
	if (!deck.stories.some(story => story.id === storyId)) return deck
	return produce(deck, draft => {
		Object.assign(draft.stories.find(story => story.id === storyId)!, patch)
	})
}

export const moveStory = (deck: DeckDefinition, storyId: string, toIndex: number): DeckDefinition => {
	const from = deck.stories.findIndex(story => story.id === storyId)
	if (from < 0) return deck
	const to = Math.max(0, Math.min(toIndex, deck.stories.length - 1))
	if (from === to) return deck
	return produce(deck, draft => moveWithin(draft.stories, from, to))
}

export const deleteStory = (deck: DeckDefinition, storyId: string): DeckDefinition => {
	if (!deck.stories.some(story => story.id === storyId)) return deck
	return produce(deck, draft => {
		draft.stories = draft.stories.filter(story => story.id !== storyId)
	})
}

// =============================================================================
// Slides
// =============================================================================

/** Refused (same deck back) at SLIDE_CAP, which counts the whole deck. */
export const addSlide = (
	deck: DeckDefinition,
	storyId: string,
	id: string,
	{ layout, index }: { layout?: SlideLayout; index?: number } = {},
): DeckDefinition => {
	const storyIndex = deck.stories.findIndex(story => story.id === storyId)
	if (storyIndex < 0 || countSlides(deck) >= SLIDE_CAP) return deck
	return produce(deck, draft => {
		const slides = draft.stories[storyIndex].slides
		slides.splice(clampIndex(index, slides.length), 0, makeSlide(id, layout))
	})
}

export const updateSlide = (
	deck: DeckDefinition,
	slideId: string,
	patch: Partial<Pick<Slide, 'title' | 'subtitle' | 'speakerNotes' | 'transition'>>,
): DeckDefinition => {
	const found = findSlide(deck, slideId)
	if (!found) return deck
	return produce(deck, draft => {
		Object.assign(draft.stories[found.storyIndex].slides[found.slideIndex], patch)
	})
}

/** Move a slide within its story or into another one (the sorter's drag). */
export const moveSlide = (deck: DeckDefinition, slideId: string, toStoryId: string, toIndex: number): DeckDefinition => {
	const found = findSlide(deck, slideId)
	const toStoryIndex = deck.stories.findIndex(story => story.id === toStoryId)
	if (!found || toStoryIndex < 0) return deck
	if (found.storyIndex === toStoryIndex && found.slideIndex === Math.min(toIndex, found.story.slides.length - 1)) return deck

	return produce(deck, draft => {
		const [slide] = draft.stories[found.storyIndex].slides.splice(found.slideIndex, 1)
		const target = draft.stories[toStoryIndex].slides
		target.splice(clampIndex(toIndex, target.length), 0, slide)
	})
}

/**
 * Copy a slide, directly after the original, with fresh ids for the slide and
 * every block on it. Ids are unique across the whole deck, so a copy that kept
 * them would make one of the two unreachable.
 *
 * `makeId` is called once per new id; the hook passes ids it minted OUTSIDE
 * the reducer, for the reason given at the top of this file. Refused at
 * SLIDE_CAP.
 */
export const duplicateSlide = (deck: DeckDefinition, slideId: string, ids: { slide: string; blocks: string[] }): DeckDefinition => {
	const found = findSlide(deck, slideId)
	if (!found || countSlides(deck) >= SLIDE_CAP) return deck
	if (ids.blocks.length < found.slide.blocks.length) return deck

	// Copied from the plain base slide, outside the recipe: cloning an Immer
	// draft would copy the proxy's bookkeeping along with the data.
	const clone: Slide = structuredClone(found.slide)
	const copy: Slide = {
		...clone,
		id: ids.slide,
		blocks: clone.blocks.map((block, index) => ({ ...block, id: ids.blocks[index] })),
	}

	return produce(deck, draft => {
		draft.stories[found.storyIndex].slides.splice(found.slideIndex + 1, 0, copy)
	})
}

export const deleteSlide = (deck: DeckDefinition, slideId: string): DeckDefinition => {
	const found = findSlide(deck, slideId)
	if (!found) return deck
	return produce(deck, draft => {
		draft.stories[found.storyIndex].slides.splice(found.slideIndex, 1)
	})
}

/**
 * Change a slide's layout, re-homing any block whose region the new layout
 * does not have.
 *
 * The server rejects a block in a region its layout lacks, so leaving blocks
 * where they were would make the deck unsaveable. Displaced blocks fill the
 * new layout's EMPTY regions first, in order, and then stack in its first
 * region. Switching to a layout with no regions at all (`section`) removes the
 * slide's blocks; that is one undo away.
 */
export const setLayout = (deck: DeckDefinition, slideId: string, layout: SlideLayout): DeckDefinition => {
	const found = findSlide(deck, slideId)
	if (!found || found.slide.layout === layout) return deck

	const regions = LAYOUT_REGIONS[layout]

	return produce(deck, draft => {
		const slide = draft.stories[found.storyIndex].slides[found.slideIndex]
		slide.layout = layout

		if (regions.length === 0) {
			slide.blocks = []
			return
		}

		const occupied = new Set(slide.blocks.filter(block => regions.includes(block.region)).map(block => block.region))
		const free = regions.filter(region => !occupied.has(region))

		for (const block of slide.blocks) {
			if (!regions.includes(block.region)) block.region = free.shift() ?? regions[0]
		}
	})
}

// =============================================================================
// Blocks
// =============================================================================

/** Refused at BLOCKS_PER_SLIDE_CAP, or for a region the slide's layout lacks. */
export const addBlock = (
	deck: DeckDefinition,
	slideId: string,
	block: Block,
	{ index }: { index?: number } = {},
): DeckDefinition => {
	const found = findSlide(deck, slideId)
	if (!found) return deck
	if (found.slide.blocks.length >= BLOCKS_PER_SLIDE_CAP) return deck
	if (!LAYOUT_REGIONS[found.slide.layout].includes(block.region)) return deck

	return produce(deck, draft => {
		const blocks = draft.stories[found.storyIndex].slides[found.slideIndex].blocks
		blocks.splice(clampIndex(index, blocks.length), 0, block)
	})
}

/**
 * Move a block: to another region, another position, or another slide. The
 * same checks as adding it there apply, since that is what it amounts to.
 */
export const moveBlock = (
	deck: DeckDefinition,
	blockId: string,
	{ slideId, region, index }: { slideId?: string; region?: string; index?: number },
): DeckDefinition => {
	const found = findBlock(deck, blockId)
	if (!found) return deck

	const targetSlideId = slideId ?? found.slide.id
	const target = findSlide(deck, targetSlideId)
	if (!target) return deck

	const targetRegion = region ?? found.block.region
	if (!LAYOUT_REGIONS[target.slide.layout].includes(targetRegion)) return deck

	const sameSlide = targetSlideId === found.slide.id
	if (!sameSlide && target.slide.blocks.length >= BLOCKS_PER_SLIDE_CAP) return deck
	if (sameSlide && targetRegion === found.block.region && (index === undefined || index === found.blockIndex)) return deck

	return produce(deck, draft => {
		const [block] = draft.stories[found.storyIndex].slides[found.slideIndex].blocks.splice(found.blockIndex, 1)
		block.region = targetRegion
		const blocks = draft.stories[target.storyIndex].slides[target.slideIndex].blocks
		blocks.splice(clampIndex(index, blocks.length), 0, block)
	})
}

export const deleteBlock = (deck: DeckDefinition, blockId: string): DeckDefinition => {
	const found = findBlock(deck, blockId)
	if (!found) return deck
	return produce(deck, draft => {
		draft.stories[found.storyIndex].slides[found.slideIndex].blocks.splice(found.blockIndex, 1)
	})
}

/** Replace a block's config whole. The inspector owns merging field edits. */
export const setBlockConfig = (deck: DeckDefinition, blockId: string, config: Record<string, unknown>): DeckDefinition => {
	const found = findBlock(deck, blockId)
	if (!found) return deck
	return produce(deck, draft => {
		draft.stories[found.storyIndex].slides[found.slideIndex].blocks[found.blockIndex].config = config
	})
}

/** Point a block at an asset, or back at its story's anchor with null. */
export const setBlockSource = (deck: DeckDefinition, blockId: string, source: EntityRef | null): DeckDefinition => {
	const found = findBlock(deck, blockId)
	if (!found) return deck
	return produce(deck, draft => {
		draft.stories[found.storyIndex].slides[found.slideIndex].blocks[found.blockIndex].source = source
	})
}
